import React, { useMemo, useState, useRef, useLayoutEffect } from 'react';
import { FolderOpen, ChartBar, UsersFour, Sparkle, Gavel, SquaresFour, ArrowRight, Package, Check, Handshake, Buildings, UserCircle, Users, House, Signature, IdentificationCard, CreditCard, Hash, Receipt, CalendarBlank, CalendarDots, CalendarCheck, Percent, Bank, User, ClipboardText, Factory, RocketLaunch, Globe, MagnifyingGlass, Trademark, ChatCircleDots, ArrowClockwise, Lightbulb, Copyright, ForkKnife, Storefront, Coins, ShieldCheck, FirstAidKit, Wallet, BookOpen, Calculator, SealCheck, ListChecks, ChartLine, ChartPieSlice, Gift, Stack, LockKey, Briefcase, UsersThree, Files, NotePencil, Scales, ArrowsLeftRight, Shield, FileText, Archive } from '@phosphor-icons/react';
import { useCurrency } from '@/hooks/useCurrency';

// Presentation groups use the catalog name; the original catalog IDs and prices
// remain authoritative for requests and partner comparison.
const GROUPS = [
  { label: 'Finance & tax', Icon: Calculator, CategoryIcon: ChartBar, tone: 'blue', match: /account|bookkeep|tax|gst|tds|audit|financial|itr/i },
  { label: 'People & equity', Icon: UsersThree, CategoryIcon: UsersFour, tone: 'violet', match: /esop|payroll|hiring|hr|employee|esi[c]?|provident|pf |cap table/i },
  { label: 'Intellectual property', Icon: Lightbulb, CategoryIcon: Sparkle, tone: 'amber', match: /trademark|copyright|patent|intellectual|\bip\b/i },
  { label: 'Company & legal', Icon: Scales, CategoryIcon: Gavel, tone: 'teal', match: /incorporat|registration|llp|company|compliance|roc|filing|agreement|contract|director|dsc|closure|winding/i },
];
export const serviceGroup = (item) => GROUPS.find(group => group.match.test(item.name || '')) || { label: 'Other services', Icon: Package, CategoryIcon: FolderOpen, tone: 'slate' };

const SERVICE_ICONS = {
  "llp registration": Handshake,
  "private limited company registration": Buildings,
  "one person company (opc) registration": UserCircle,
  "partnership firm registration": Users,
  "sole proprietorship registration": House,
  "digital signature certificate (dsc)": Signature,
  "director identification number (din)": IdentificationCard,
  "pan application (entity)": CreditCard,
  "tan application": Hash,
  "gst registration": Receipt,
  "gst filing (monthly)": CalendarBlank,
  "gst filing (quarterly)": CalendarDots,
  "gst annual return": CalendarCheck,
  "tds return filing": Percent,
  "income tax return \u2014 company": Bank,
  "income tax return \u2014 founder": User,
  "roc annual compliance": ClipboardText,
  "msme / udyam registration": Factory,
  "startup india registration": RocketLaunch,
  "import export code (iec)": Globe,
  "trademark search & opinion": MagnifyingGlass,
  "trademark registration": Trademark,
  "trademark objection response": ChatCircleDots,
  "trademark renewal": ArrowClockwise,
  "patent filing": Lightbulb,
  "copyright registration": Copyright,
  "fssai registration": ForkKnife,
  "shop & establishment license": Storefront,
  "professional tax registration": Coins,
  "pf registration": ShieldCheck,
  "esic registration": FirstAidKit,
  "payroll processing": Wallet,
  "bookkeeping (monthly)": BookOpen,
  "accounting setup": Calculator,
  "statutory audit": SealCheck,
  "internal audit": ListChecks,
  "fractional cfo": ChartLine,
  "cap table management": ChartPieSlice,
  "esop plan setup": Gift,
  "share allotment process": Stack,
  "nda drafting": LockKey,
  "employment contract drafting": Briefcase,
  "founders' agreement": UsersThree,
  "vendor & customer contract templates": Files,
  "term sheet review": NotePencil,
  "investment agreement review": Scales,
  "safe / convertible note drafting": ArrowsLeftRight,
  "privacy policy drafting": Shield,
  "terms of service drafting": FileText,
  "closure / winding-up filing": Archive,
};

export const serviceIcon = (item) => SERVICE_ICONS[(item.name || "").trim().toLowerCase()] || serviceGroup(item).Icon;

export function ServiceMarketplaceSummary({ vendors, items, totalOfferings, loading }) {
  return <section className="services-overview" aria-label="Marketplace summary" data-testid="marketplace-service-hero">
    <span className="services-overview-icon"><Storefront size={24} /></span>
    <div className="services-overview-copy"><h2>Startup services</h2><p>From incorporation to compliance. Expert partners, one managed workflow.</p></div>
    <dl className="services-stats">
      {[['Live offerings', totalOfferings, 'offerings'], ['Partners', vendors.length, 'vendors'], ['Services', items.length, 'products']].map(([label, count, id]) => <div key={id} data-testid={`marketplace-service-stat-${id}`}><dt>{label}</dt><dd>{loading ? '—' : count}</dd></div>)}
    </dl>
  </section>;
}

export default function ServiceMarketplaceCatalog({ items, loading, error, onRetry, onPick }) {
  const { format } = useCurrency();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const catalogRef = useRef(null);
  const shouldScroll = useRef(false);
  const selectCategory = category => {
    shouldScroll.current = true;
    setSelectedCategory(category);
    if (category === selectedCategory) {
      catalogRef.current?.scrollIntoView({ block: 'start', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      shouldScroll.current = false;
    }
  };
  useLayoutEffect(() => {
    if (!shouldScroll.current) return;
    shouldScroll.current = false;
    catalogRef.current?.scrollIntoView({ block: 'start', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
  }, [selectedCategory]);
  const categoryOptions = useMemo(() => [...GROUPS, { label: 'Other services', CategoryIcon: FolderOpen }].filter(group => items.some(item => serviceGroup(item).label === group.label)), [items]);
  const sections = useMemo(() => {
    const labels = [...GROUPS.map(group => group.label), 'Other services'];
    const grouped = (available) => labels.filter(label => selectedCategory === 'all' || label === selectedCategory).map(label => ({ label, items: items.filter(item => (item.vendor_count > 0) === available && serviceGroup(item).label === label) })).filter(group => group.items.length);
    return { available: grouped(true), comingSoon: grouped(false) };
  }, [items, selectedCategory]);
  const renderGroup = (section, soon = false) => {
    const { CategoryIcon, tone } = serviceGroup(section.items[0]);
    return <section key={section.label} className={`services-category-section tone-${tone}`} aria-label={`${section.label}${soon ? ' coming soon' : ''}`}>
      <div className="services-category-header">
        <span className="services-product-icon services-category-icon" aria-hidden="true"><CategoryIcon size={20} /></span>
        <h3 className="services-category-heading">{section.label}</h3>
        <span className="services-category-count">{section.items.length} service{section.items.length === 1 ? '' : 's'}</span>
        <span className="services-category-rule" aria-hidden="true" />
      </div>
      <ServiceCards items={section.items} onPick={onPick} format={format} />
    </section>;
  };
  return <section ref={catalogRef} className="services-catalog" aria-labelledby="services-offerings-title">
    <div className="services-catalog-toolbar"><div><h2 id="services-offerings-title">Service offerings <span>{loading ? '—' : items.length}</span></h2><p>Choose a service to compare partners and pricing.</p></div></div>
    {!error && <div className="services-category-pills" role="group" aria-label="Filter services by category">
      <button type="button" aria-pressed={selectedCategory === 'all'} onClick={() => selectCategory('all')}><SquaresFour size={15} />All services</button>
      {categoryOptions.map(({ label, CategoryIcon }) => <button type="button" key={label} aria-pressed={selectedCategory === label} onClick={() => selectCategory(label)}><CategoryIcon size={15} />{label}</button>)}
    </div>}
    {loading ? <div className="services-product-grid" aria-busy="true" aria-label="Loading services">{[0,1,2,3,4,5].map(i => <div key={i} className="services-card-skeleton"><span /><div /><div /><div /></div>)}</div>
      : error ? <div className="services-empty" role="alert"><ShieldCheck size={28} /><h3>Services couldn’t load</h3><p>{error}</p><button className="services-control" onClick={onRetry}>Try again</button></div>
      : !items.length ? <div className="services-empty"><Package size={28} /><h3>Services are on their way</h3><p>Check back soon for available partners and pricing.</p></div>
      : <div data-testid="marketplace-product-grid">
          {sections.available.map(section => renderGroup(section))}
          {sections.comingSoon.length > 0 && <section className="services-coming-soon" aria-labelledby="services-coming-soon-title" data-testid="services-coming-soon">
            <div className="services-catalog-toolbar"><div><h2 id="services-coming-soon-title">Coming soon</h2><p>More expert support is on the way.</p></div></div>
            {sections.comingSoon.map(section => renderGroup(section, true))}
          </section>}
        </div>}
  </section>;
}

function ServiceCards({ items, onPick, format }) {
  return <div className="services-product-grid">{items.map(item => {
        const { tone } = serviceGroup(item);
        const Icon = serviceIcon(item);
        const available = item.vendor_count > 0;
        const price = item.min_total ?? item.min_price;
        return <button type="button" key={item.id} className={`services-product-card tone-${tone}`} disabled={!available} onClick={() => onPick(item.id)} data-testid={`marketplace-product-card-${item.id}`}>
          <div className="services-card-top"><span className="services-product-icon"><Icon size={22} /></span><span className="services-price">{available && price != null ? <>From <strong>{format(price, item.currency || 'INR')}</strong></> : '—'}</span></div>
          <h3>{item.name}</h3><p className="services-description">{item.description || 'Expert support for your startup.'}</p>
          {available && item.min_total != null && item.min_total > (item.min_price || 0) && <div className="services-card-meta"><span>Incl. govt fees</span></div>}
          <div className="services-card-footer"><span className={`services-availability ${available ? 'available' : ''}`}>{available && <Check size={11} />} {available ? 'Available' : 'Coming soon'}</span><span className="services-compare">{available ? 'Compare' : 'Not yet available'}{available && <ArrowRight size={14} />}</span></div>
        </button>;
      })}</div>;
}
